import React, { useEffect, useRef, useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
  IonModal,
  IonNote,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import {
  getAvailableStockAtLocation,
  recordTicketInventoryUsage,
} from '../services/ticket.service';
import { getInventoryLocationsPaginated } from '../services/device.service';
import SelectItem from './SelectItem';

interface IProps {
  ticketId: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const RecordPartsUsed: React.FC<IProps> = ({ ticketId, isOpen, onClose, onCreated }) => {
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [location, setLocation] = useState<{ id: string; name: string } | null>(null);
  const [availableItems, setAvailableItems] = useState<{ itemId: string; name: string; available: number }[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const latestRequestRef = useRef<string>('');

  const [itemId, setItemId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [comments, setComments] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const selectedAvailable = availableItems.find((i) => i.itemId === itemId)?.available ?? 0;
  const qtyNum = Number(quantity);
  const valid = !!location?.id && !!itemId && qtyNum >= 1 && qtyNum <= selectedAvailable;

  const reset = () => {
    setLocation(null);
    setAvailableItems([]);
    setItemId('');
    setQuantity('');
    setComments('');
    setError('');
  };

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen]);

  const loadAvailableItems = async (locationId: string) => {
    latestRequestRef.current = locationId;
    setLoadingItems(true);
    setAvailableItems([]);
    setItemId('');
    try {
      const res = await getAvailableStockAtLocation(ticketId, locationId);
      if (latestRequestRef.current !== locationId) return;
      setAvailableItems(res.items ?? []);
    } catch {
      if (latestRequestRef.current !== locationId) return;
      setAvailableItems([]);
    } finally {
      if (latestRequestRef.current === locationId) setLoadingItems(false);
    }
  };

  const handleSubmit = async () => {
    if (!valid || !location) return;
    setSubmitting(true);
    try {
      await recordTicketInventoryUsage(ticketId, {
        inventoryItemId: itemId,
        fromInventoryLocationId: location.id,
        quantity: qtyNum,
        comments: comments.trim() || undefined,
      });
      onCreated();
      onClose();
    } catch {
      setError('Failed to record parts used');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Record Parts Used</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={handleClose}>Close</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonItem button onClick={() => setShowLocationPicker(true)}>
            <IonLabel position="stacked">Source Location</IonLabel>
            <IonInput readonly value={location?.name ?? ''} placeholder="Tap to select" />
          </IonItem>

          {location && (
            <>
              <IonItem
                button
                disabled={loadingItems || availableItems.length === 0}
                onClick={() => setShowItemPicker(true)}
              >
                <IonLabel position="stacked">Item</IonLabel>
                <IonInput
                  readonly
                  value={availableItems.find((i) => i.itemId === itemId)?.name ?? ''}
                  placeholder={
                    loadingItems
                      ? 'Loading items…'
                      : availableItems.length === 0
                      ? 'No items with stock at this location'
                      : 'Tap to select'
                  }
                />
                {itemId && (
                  <IonNote slot="end" color="medium">avail: {selectedAvailable}</IonNote>
                )}
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Quantity</IonLabel>
                <IonInput
                  type="number"
                  value={quantity}
                  onIonChange={(e) => setQuantity(e.detail.value ?? '')}
                  placeholder="e.g. 1"
                  min="1"
                  max={selectedAvailable ? String(selectedAvailable) : undefined}
                />
                {itemId && (
                  <IonNote slot="helper">Max available: {selectedAvailable}</IonNote>
                )}
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Notes (optional)</IonLabel>
                <IonTextarea
                  value={comments}
                  onIonChange={(e) => setComments(e.detail.value ?? '')}
                  placeholder="e.g. Replaced faulty sensor"
                  rows={2}
                />
              </IonItem>
            </>
          )}

          <IonButton
            expand="block"
            className="ion-margin-top"
            onClick={handleSubmit}
            disabled={!valid || submitting}
          >
            {submitting ? 'Recording…' : 'Record Usage'}
          </IonButton>
        </IonContent>
      </IonModal>

      <IonModal isOpen={showLocationPicker} onDidDismiss={() => setShowLocationPicker(false)}>
        <SelectItem
          label="Select Source Location"
          selectedValue={location?.id ?? ''}
          labelKey="name"
          valueKey="id"
          setItem={(item: any) => {
            const picked = { id: item.id, name: item.name };
            setLocation(picked);
            setShowLocationPicker(false);
            loadAvailableItems(picked.id);
          }}
          onClose={() => setShowLocationPicker(false)}
          getPaginatedItems={getInventoryLocationsPaginated}
        />
      </IonModal>

      <IonModal isOpen={showItemPicker} onDidDismiss={() => setShowItemPicker(false)}>
        <SelectItem
          label="Select Item"
          selectedValue={itemId}
          labelKey="displayLabel"
          valueKey="itemId"
          items={availableItems.map((i) => ({
            ...i,
            displayLabel: `${i.name} (avail: ${i.available})`,
          }))}
          setItem={(item: any) => {
            setItemId(item.itemId);
            setShowItemPicker(false);
          }}
          onClose={() => setShowItemPicker(false)}
        />
      </IonModal>

      <IonLoading isOpen={submitting} message="Recording…" />

      <IonToast
        isOpen={!!error}
        onDidDismiss={() => setError('')}
        message={error}
        duration={2500}
        color="danger"
      />
    </>
  );
};

export default RecordPartsUsed;
